import { useCallback, useRef, useEffect } from 'react'
import { cn } from '@/true-fsd/shared/utils/css'
import { TEXT_EDITOR } from '../constants'
import classes from './TextEditor.module.scss'

/**
 * Пропсы для компонента TextEditor
 */
export interface TextEditorProps {
    /**
     * Текущее значение текста (может быть null)
     */
    value: string | null

    /**
     * Обработчик изменения текста
     */
    onChange: (value: string) => void

    /**
     * Плейсхолдер для поля ввода
     */
    placeholder?: string

    /**
     * Дополнительные CSS классы
     */
    className?: string

    /**
     * Отключено ли поле ввода
     */
    disabled?: boolean

    /**
     * Скрыть ли label
     */
    hideLabel?: boolean
}

/**
 * Компонент для редактирования текста маркировки
 */
export function TextEditor({
    value,
    onChange,
    placeholder = 'Введите текст маркировки',
    className,
    disabled = false,
    hideLabel = false,
}: TextEditorProps) {
    /**
     * Обработчик изменения значения поля ввода
     */
    const handleChange = useCallback(
        (newValue: string) => {
            onChange(newValue)
        },
        [onChange]
    )

    /**
     * Обработчик нажатия клавиш
     * Обеспечивает корректную обработку Enter для переноса строки
     */
    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
            // При нажатии Enter вставляем перенос строки
            // textarea делает это автоматически, но мы можем добавить дополнительную логику
            if (e.key === 'Enter') {
                // Разрешаем стандартное поведение (вставка переноса строки)
                // useEffect автоматически пересчитает размер шрифта при изменении значения
            }
        },
        []
    )

    // Преобразуем null в пустую строку
    const inputValue = value ?? ''
    const textareaRef = useRef<HTMLTextAreaElement>(null)

    // Адаптивный размер шрифта при вводе текста
    // Автоматически подбирает максимальный размер шрифта, чтобы весь текст помещался внутри окна
    // Текст должен переноситься на новую строку и не выходить за границы
    useEffect(() => {
        const textarea = textareaRef.current
        if (!textarea) return

        const minFontSize = TEXT_EDITOR.MIN_FONT_SIZE
        const maxFontSize = TEXT_EDITOR.MAX_FONT_SIZE
        const fontSizeStep = TEXT_EDITOR.FONT_SIZE_STEP
        const lineHeight = TEXT_EDITOR.LINE_HEIGHT
        const paddingHorizontal = TEXT_EDITOR.PADDING_HORIZONTAL

        // Ждем следующего кадра для правильного измерения
        requestAnimationFrame(() => {
            if (!textarea) return

            // Если нет текста, используем минимальный размер и центрируем плейсхолдер
            if (!inputValue || inputValue.trim() === '') {
                textarea.style.fontSize = `${minFontSize}px`
                // Для плейсхолдера используем line-height равный высоте для вертикального центрирования
                textarea.style.lineHeight = `${textarea.clientHeight}px`
                return
            }

            // Учитываем padding при расчете доступной ширины и высоты
            const maxWidth =
                textarea.clientWidth - paddingHorizontal * 2 // Учитываем padding слева и справа
            const maxHeight = textarea.clientHeight // Полная высота textarea

            // Создаем временный элемент для измерения текста
            const measureEl = document.createElement('div')
            measureEl.style.position = 'absolute'
            measureEl.style.visibility = 'hidden'
            measureEl.style.whiteSpace = 'pre-wrap' // Разрешаем перенос строк
            measureEl.style.wordWrap = 'break-word' // Перенос слов
            measureEl.style.overflowWrap = 'break-word' // Перенос длинных слов
            measureEl.style.textAlign = 'center' // Центрирование для правильного измерения
            measureEl.style.width = `${maxWidth}px`
            measureEl.style.fontFamily = getComputedStyle(textarea).fontFamily
            measureEl.style.lineHeight = `${lineHeight}`
            measureEl.style.padding = '0'
            measureEl.style.margin = '0'
            measureEl.style.border = 'none'
            measureEl.textContent = inputValue
            document.body.appendChild(measureEl)

            let fontSize = minFontSize
            let bestFitFontSize = minFontSize

            // Увеличиваем размер шрифта пока весь текст помещается
            // Находим максимальный размер, при котором весь текст полностью помещается
            while (fontSize <= maxFontSize) {
                measureEl.style.fontSize = `${fontSize}px`
                const height = measureEl.offsetHeight
                const width = measureEl.scrollWidth // Используем scrollWidth для точного измерения ширины

                // Проверяем, помещается ли весь текст по высоте и ширине
                // Текст должен помещаться в maxHeight и не выходить за maxWidth
                if (height <= maxHeight && width <= maxWidth) {
                    bestFitFontSize = fontSize
                    fontSize += fontSizeStep
                } else {
                    // Если не помещается, останавливаемся на предыдущем размере
                    break
                }
            }

            document.body.removeChild(measureEl)

            // Устанавливаем найденный оптимальный размер шрифта
            textarea.style.fontSize = `${bestFitFontSize}px`
            // Адаптируем line-height пропорционально размеру шрифта
            textarea.style.lineHeight = `${lineHeight}`
        })
    }, [inputValue, placeholder])

    return (
        <div className={cn(classes.textEditorContainer, className)}>
            {!hideLabel && (
                <label className={classes.label} htmlFor="marking-text-input">
                    Текст маркировки
                </label>
            )}
            <textarea
                ref={textareaRef}
                id="marking-text-input"
                value={inputValue}
                onChange={(e) => handleChange(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={placeholder}
                disabled={disabled}
                className={classes.textarea}
                aria-label="Текст маркировки"
            />
        </div>
    )
}
